import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Tag as TagIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { utalkService } from '../services/utalkService';
import { 
  BulkMessageFormData, 
  Contact, 
  ValidationResult,
  ContactValidation 
} from '../types/bulkMessage.types';

const BulkMessageForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([
    { name: '', phoneNumber: '', variables: {} }
  ]);

  const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<BulkMessageFormData>({
    defaultValues: {
      channelId: '',
      name: '',
      templateMessageId: '',
      scheduledTo: '',
      tags: [],
      contacts: []
    }
  });

  // Verificar configuração da API ao carregar
  useEffect(() => {
    const configValidation = utalkService.validateConfiguration();
    if (!configValidation.isValid) {
      setError(configValidation.message);
    }
  }, []);

  // Adicionar novo contato
  const addContact = () => {
    setContacts([...contacts, { name: '', phoneNumber: '', variables: {} }]);
  };

  // Remover contato
  const removeContact = (index: number) => {
    if (contacts.length > 1) {
      const newContacts = contacts.filter((_, i) => i !== index);
      setContacts(newContacts);
      setValue('contacts', newContacts);
    }
  };

  // Atualizar contato
  const updateContact = (index: number, field: keyof Contact, value: string) => {
    const newContacts = [...contacts];
    if (field === 'variables') {
      // Para variables, mantemos como string no estado local para o formulário
      (newContacts[index] as any)[field] = value;
    } else {
      newContacts[index] = { ...newContacts[index], [field]: value };
    }
    setContacts(newContacts);
    setValue('contacts', newContacts);
  };

  // Validar contato individual
  const validateContact = (contact: Contact): ContactValidation => {
    const errors: string[] = [];

    if (!contact.name.trim()) {
      errors.push('Nome é obrigatório');
    }

    if (!contact.phoneNumber.trim()) {
      errors.push('Telefone é obrigatório');
    } else {
      const phoneValidation = utalkService.validatePhoneNumber(contact.phoneNumber);
      if (!phoneValidation.isValid) {
        errors.push(phoneValidation.message);
      }
    }

    // Validar JSON das variáveis se fornecidas
    const variablesStr = typeof contact.variables === 'string' ? contact.variables : JSON.stringify(contact.variables);
    if (variablesStr && variablesStr.trim() && variablesStr !== '{}') {
      try {
        JSON.parse(variablesStr);
      } catch {
        errors.push('Variáveis devem ser um JSON válido');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Validar formulário completo
  const validateForm = (data: BulkMessageFormData): ValidationResult => {
    const errors: string[] = [];

    if (!data.channelId.trim()) {
      errors.push('Channel ID é obrigatório');
    }

    if (!data.name.trim()) {
      errors.push('Nome da sessão é obrigatório');
    }

    if (!data.templateMessageId.trim()) {
      errors.push('Template Message ID é obrigatório');
    }

    // Validar contatos
    if (contacts.length === 0) {
      errors.push('Pelo menos um contato deve ser fornecido');
    } else {
      contacts.forEach((contact, index) => {
        const contactValidation = validateContact(contact);
        if (!contactValidation.isValid) {
          contactValidation.errors.forEach(error => {
            errors.push(`Contato ${index + 1}: ${error}`);
          });
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Enviar mensagem em massa
  const onSubmit = async (data: BulkMessageFormData) => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      // Validar formulário
      const validation = validateForm(data);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        setLoading(false);
        return;
      }

      // Formatar contatos
      const formattedContacts = contacts.map(contact => {
        const contactData: Contact = {
          name: contact.name.trim(),
          phoneNumber: utalkService.formatPhoneNumber(contact.phoneNumber)
        };

        // Adicionar variáveis se fornecidas
        const variablesStr = typeof contact.variables === 'string' ? contact.variables : JSON.stringify(contact.variables);
        if (variablesStr && variablesStr.trim() && variablesStr !== '{}') {
          try {
            contactData.variables = JSON.parse(variablesStr);
          } catch {
            console.warn('Variáveis JSON inválidas ignoradas para contato:', contact.name);
          }
        }

        return contactData;
      });

      // Preparar dados para envio
      const requestData = {
        channelId: data.channelId.trim(),
        name: data.name.trim(),
        contacts: formattedContacts,
        templateMessageId: data.templateMessageId.trim(),
        scheduledTo: data.scheduledTo || undefined,
        tags: data.tags && data.tags.length > 0 ? data.tags : undefined
      };

      const result = await utalkService.sendBulkMessage(requestData);

      if (result.success) {
        setResponse(result.data);
      } else {
        setError(result.error || 'Erro ao enviar mensagem em massa');
      }

    } catch (err: any) {
      setError(err.message || 'Erro inesperado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Card>
        <CardHeader 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            '& .MuiCardHeader-title': { color: 'white' }
          }}
          title={
            <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
              <SendIcon />
              UTalk Bulk Message Sender
            </Typography>
          }
        />

        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Informações da Sessão */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon />
                Informações da Sessão
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="channelId"
                    control={control}
                    rules={{ required: 'Channel ID é obrigatório' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Channel ID"
                        placeholder="ID do canal"
                        error={!!errors.channelId}
                        helperText={errors.channelId?.message}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: 'Nome da sessão é obrigatório' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Nome da Sessão"
                        placeholder="Nome da sessão de envio"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="templateMessageId"
                    control={control}
                    rules={{ required: 'Template Message ID é obrigatório' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Template Message ID"
                        placeholder="ID do template de mensagem"
                        error={!!errors.templateMessageId}
                        helperText={errors.templateMessageId?.message}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="scheduledTo"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Agendar Para (ISO 8601)"
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        helperText="Deixe vazio para envio imediato"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Tags (separadas por vírgula)"
                        placeholder="marketing, promo, newsletter"
                        onChange={(e) => {
                          const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                          field.onChange(tags);
                        }}
                        helperText="Tags para categorizar a mensagem"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Contatos */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon />
                  Contatos
                </Typography>
                <Button
                  type="button"
                  onClick={addContact}
                  variant="outlined"
                  startIcon={<AddIcon />}
                  size="small"
                >
                  Adicionar Contato
                </Button>
              </Box>

              {contacts.map((contact, index) => (
                <Card key={index} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" color="primary">
                        Contato {index + 1}
                      </Typography>
                      {contacts.length > 1 && (
                        <IconButton
                          onClick={() => removeContact(index)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Nome"
                          value={contact.name}
                          onChange={(e) => updateContact(index, 'name', e.target.value)}
                          placeholder="Nome do contato"
                          InputProps={{
                            startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Telefone"
                          value={contact.phoneNumber}
                          onChange={(e) => updateContact(index, 'phoneNumber', e.target.value)}
                          placeholder="(11) 98765-4321"
                          InputProps={{
                            startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Variáveis (JSON)"
                          value={typeof contact.variables === 'string' ? contact.variables : JSON.stringify(contact.variables)}
                          onChange={(e) => updateContact(index, 'variables', e.target.value)}
                          placeholder='{"nome": "João", "produto": "Widget"}'
                          multiline
                          rows={2}
                          helperText="Variáveis personalizadas em formato JSON"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Botão de Envio */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              fullWidth
              sx={{ 
                py: 2,
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                }
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Enviando...
                </>
              ) : (
                <>
                  <SendIcon sx={{ mr: 1 }} />
                  Enviar Mensagem em Massa
                </>
              )}
            </Button>
          </form>

          {/* Exibir Resposta/Sucesso */}
          {response && (
            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                ✅ Mensagem em massa enviada com sucesso!
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="body2" component="pre" sx={{ overflow: 'auto', fontSize: '0.875rem' }}>
                  {JSON.stringify(response, null, 2)}
                </Typography>
              </Box>
            </Alert>
          )}

          {/* Exibir Erro */}
          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                ❌ Erro ao enviar mensagem
              </Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BulkMessageForm;
